import React, { Fragment, useRef, useState } from 'react'
import { Box, Typography, TextField, Collapse, Grid } from '@mui/material'
import { useStyles } from '../styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { SkuItem } from './SkuItem'
import { ApiConfig } from '../../../../config/api'
import axios from 'axios'
import { useApi } from '../../../../hooks/useApi'
import { Status } from '../../../../hooks/useFetchState'
import { LoadingButton } from '../../../../components/_EOS/Button/LoadingButton'
import { ApiResponse } from '../../../../typings/ApiResponse'
import { Product } from '../../../Productos/entities/Product'
import { StepProps } from '../../../../components/_WMS/Stepper'
import {
  eInventoryCheckSteps,
  SharedStateType,
  SkuBatchInfoDto
} from '../../types'
import { Modal } from '../../../../components/_EOS/Modal'
import { ListBatch } from './ListBatch'
import { useModal } from '../../../../hooks/useModal'
import { useProductOrigins } from '../../../../hooks'
import {
  ExpiredColorStatus,
  getExpiredStatusToday,
  getStyleRow
} from '../../../../utils/colors'
interface ScanSkuProps extends StepProps<SharedStateType> {}

const getValidateSkuOnInventoryCheck = (sku: string, positionId: number) => {
  const url = new URL(
    `${ApiConfig.InventoryCheckApi}/sku/${sku}?positionId=${positionId}`
  )

  return axios.get(url.toString())
}

const postCloseInventoryCheckPositionCycle = (
  inventoryCheckDetailId: number,
  list: {
    skuId: number
    checkedQty: number
    batchId?: number | null
  }[]
) => {
  const url = new URL(`${ApiConfig.InventoryCheckDetailItemApi}`)

  const body = {
    inventoryCheckDetailId,
    inventoryCheckDetailItems: list
  }

  return axios.post(url.toString(), body)
}

export const ScanSku: React.FC<ScanSkuProps> = ({
  sharedState: {
    skuList,
    workOrderId,
    user,
    positionCode,
    positionId,
    inventoryCheckDetailId,
    selectedSku,
    batchList
  },
  setSharedState,
  goToStep
}) => {
  const skuInputRef = useRef<HTMLInputElement>(null)
  const { state, callApi } = useApi<ApiResponse<Product>>()
  const {
    state: postCloseInventoryCheckItemsState,
    callApi: postCloseInventoryCheckItemsCallApi
  } = useApi<ApiResponse<Product>>()

  const classes = useStyles()
  const [scannedSku, setScannedSku] = useState('')
  const [expanded, setExpanded] = useState(false)

  const requestValidateSkuOnInventoryCheck = async (
    sku: string
  ): Promise<SkuBatchInfoDto | null> => {
    if (!workOrderId) return null

    return await callApi(getValidateSkuOnInventoryCheck(sku, positionId || 0))
      .then((data) => {
        return data as SkuBatchInfoDto
      })
      .catch((error) => {
        setSharedState((prevState) => ({
          ...prevState,
          error
        }))
        return null
      })
  }

  const requestGetCloseInventoryCheckPositionCycle = () => {
    const list = (skuList || []).map((el) => ({
      skuId: el.skuId || 0,
      checkedQty: el.checkedQty,
      batchId: el.batchId
    }))

    postCloseInventoryCheckItemsCallApi(
      postCloseInventoryCheckPositionCycle(inventoryCheckDetailId || 0, list)
    )
      .then(() => {
        setSharedState((prevState) => ({ ...prevState, skuList: undefined }))
        goToStep(eInventoryCheckSteps.SuccessInventoryCheck)
      })
      .catch((err) => {
        setSharedState((prevState) => ({ ...prevState, error: err }))
        goToStep(eInventoryCheckSteps.Error)
      })
  }

  const handleScanSku = async (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault()

    if (scannedSku.length <= 0) {
      return
    }

    const existingSkuIndex = skuList?.findIndex(
      (item) => item.sku === scannedSku
    )

    // si es item con lote, se debe validar siempre
    const withBatch = skuList?.find((it) => it.sku === scannedSku)?.batchId

    if (existingSkuIndex !== undefined && existingSkuIndex >= 0 && !withBatch) {
      const existingSku = skuList![existingSkuIndex]
      if (existingSku.validated) {
        setSharedState((prev) => {
          const updatedSkuList = prev.skuList!.map((item, index) =>
            index === existingSkuIndex
              ? { ...item, checkedQty: item.checkedQty + 1 } // Asegúrate de que sea `checkedQty` en lugar de `count`
              : item
          )
          return {
            ...prev,
            skuList: updatedSkuList
          }
        })

        setScannedSku('')
      } else {
        alert('SKU no está validado')
      }
    } else {
      const stockKeepingUnitId = await requestValidateSkuOnInventoryCheck(
        scannedSku
      )

      if (!stockKeepingUnitId) {
        goToStep(eInventoryCheckSteps.Error)
        return
      }

      if (stockKeepingUnitId.batches) {
        setSharedState((prev) => ({
          ...prev,
          selectedSku: stockKeepingUnitId,
          batchList: stockKeepingUnitId.batches
        }))

        selectBatchModalState.openModal(
          'sm',
          { id: stockKeepingUnitId.skuId },
          `Seleccionar Lote: ${stockKeepingUnitId.sku}`
        )

        setScannedSku('')

        // goToStep(eInventoryCheckSteps.ListBatch)
        return
      }

      setScannedSku('')

      setSharedState((prev) => {
        const updatedSkuList =
          existingSkuIndex !== undefined && existingSkuIndex >= 0
            ? prev.skuList!.map((item, index) =>
                index === existingSkuIndex
                  ? {
                      ...item,
                      count: item.checkedQty + 1,
                      validated: true,
                      skuId: stockKeepingUnitId.skuId,
                      productOriginId: stockKeepingUnitId.productOriginId
                    }
                  : item
              )
            : [
                ...(prev.skuList || []),
                {
                  sku: scannedSku,
                  skuId: stockKeepingUnitId.skuId,
                  checkedQty: 1,
                  validated: true,
                  productOriginId: stockKeepingUnitId.productOriginId
                }
              ]

        return { ...prev, skuList: updatedSkuList }
      })

      skuInputRef.current?.focus()
    }
  }

  // Obtenemos productOrigin
  const {
    productOriginsState: { productOrigins }
  } = useProductOrigins()

  const closeModal = () => {
    selectBatchModalState.closeModal()
    skuInputRef.current?.focus()
  }

  const handleExpandClick = () => {
    setExpanded((prev) => !prev)
  }

  const handleDeleteSku = (sku: string) => {
    setSharedState((prev) => ({
      ...prev,
      skuList: prev.skuList?.filter((item) => item.sku !== sku)
    }))
  }

  const selectBatchModalState = useModal<{
    id: number
  }>('')

  // Determinar el estilo de fondo basado en fecha de expiración
  const getBackgroundStyle = (
    expirationDate: string | undefined,
    skuProductOrigin: number
  ) => {
    if (expirationDate) {
      return getStyleRow(
        getExpiredStatusToday(expirationDate, skuProductOrigin)
      )
    }
    return { backgroundColor: '#f5f5f5', borderBottom: '1px solid #ccc' } // Gris por defecto
  }

  // Determinar el color de texto basado en fecha de expiración
  const getTextColor = (
    expirationDate: string | undefined,
    skuProductOrigin: number
  ) => {
    if (expirationDate) {
      const status = getExpiredStatusToday(expirationDate, skuProductOrigin)
      if (status === ExpiredColorStatus.expired) {
        return '#c62828' // Rojo oscuro para fondo rojo pastel
      }
      if (status === ExpiredColorStatus.expireSoon) {
        return '#e79f31' // Naranja oscuro para fondo naranja pastel
      }
    }
    return '#5C8E2F' // Verde por defecto
  }

  return (
    <Fragment>
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography variant='body1'>Operador {user?.userAlias}</Typography>
          <Typography variant='body1'>OT {workOrderId}</Typography>
        </Box>
        <Box className={classes.header}>
          <Typography variant='body1'>Posición {positionCode}</Typography>
        </Box>
        <Box className={classes.content}>
          <Box className={classes.formControl}>
            <form onSubmit={handleScanSku}>
              <TextField
                inputRef={skuInputRef}
                autoFocus
                variant='outlined'
                label='Escanear SKU'
                value={scannedSku}
                onChange={(e) => setScannedSku(e.target.value)}
                disabled={
                  state.status === Status.Loading ||
                  postCloseInventoryCheckItemsState.status === Status.Loading
                }
              />
            </form>
            <Box className={classes.buttonContainer}>
              <LoadingButton
                variant='outlined'
                color='secondary'
                onClick={() =>
                  goToStep(eInventoryCheckSteps.SearchInventoryCheck)
                }
                disabled={
                  state.status === Status.Loading ||
                  postCloseInventoryCheckItemsState.status === Status.Loading
                }
              >
                Cancelar
              </LoadingButton>
              <LoadingButton
                loading={state.status === Status.Loading}
                variant='contained'
                color='primary'
                disabled={
                  state.status === Status.Loading ||
                  postCloseInventoryCheckItemsState.status === Status.Loading
                }
                onClick={handleScanSku}
              >
                Confirmar
              </LoadingButton>
            </Box>
            <Box className={classes.buttonContainer}>
              <LoadingButton
                loading={
                  postCloseInventoryCheckItemsState.status === Status.Loading
                }
                fullWidth
                variant='outlined'
                color='inherit'
                onClick={requestGetCloseInventoryCheckPositionCycle}
                disabled={state.status === Status.Loading}
              >
                Finalizar Posición
              </LoadingButton>
            </Box>
          </Box>
        </Box>

        {skuList && skuList.length > 0 && (
          <Box className={classes.footer}>
            <Box className={classes.expandButton} onClick={handleExpandClick}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              {!expanded && (
                <SkuItem
                  sku={skuList[skuList.length - 1]}
                  isCollapsed={true}
                  productOrigins={productOrigins}
                  getBackgroundStyle={getBackgroundStyle}
                  getTextColor={getTextColor}
                />
              )}
            </Box>
            <Collapse in={expanded} timeout='auto' unmountOnExit>
              <Grid container spacing={1} className={classes.listContainer}>
                {skuList.map((sku, index) => (
                  <Fragment key={index}>
                    <Grid item xs={12}>
                      <SkuItem
                        sku={sku}
                        onDelete={handleDeleteSku}
                        disabled={
                          state.status === Status.Loading ||
                          postCloseInventoryCheckItemsState.status ===
                            Status.Loading
                        }
                        productOrigins={productOrigins}
                        getBackgroundStyle={getBackgroundStyle}
                        getTextColor={getTextColor}
                      />
                    </Grid>
                  </Fragment>
                ))}
              </Grid>
            </Collapse>
          </Box>
        )}
      </Box>
      <Modal
        open={selectBatchModalState.isOpen}
        title={selectBatchModalState.title}
        maxWidth={selectBatchModalState.size}
        onClose={selectBatchModalState.closeModal}
        disableEnforceFocus
        disableRestoreFocus
      >
        <ListBatch
          setSharedState={setSharedState}
          handleCloseModal={closeModal}
          batchList={batchList || []}
          skuList={skuList || []}
          selectedSku={selectedSku}
          positionCode={positionCode || ''}
          getBackgroundStyle={getBackgroundStyle}
          productOrigins={productOrigins}
        />
      </Modal>
    </Fragment>
  )
}
